import { FormattedMessage } from 'react-intl';
import { Link, NavLink } from 'react-router';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import IframeResizer, { type IframeResizerRef } from '@rio-cloud/iframe-resizer';

import { config } from '../../config';
import { routes } from '../../routes/routes';
import { useAppSelector } from '../../configuration/setup/hooks.ts';
import { getIdToken } from '../../configuration/tokenHandling/tokenSlice.ts';
import { type RefObject, useRef, useState } from 'react';
import useIncomingPostMessages from '@rio-cloud/rio-uikit/useIncomingPostMessages';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

type MenuLinkClickedMessage = {
    type: 'MENU_LINK_CLICKED';
    href?: string;
    requestId?: string;
};

const getMenuOrigin = (menuUrl: string | undefined) => {
    if (!menuUrl) {
        return undefined;
    }

    try {
        return new URL(menuUrl).origin;
    } catch {
        return undefined;
    }
};

const getNavigationUrl = (href: string | undefined): string | undefined => {
    if (!href) {
        return undefined;
    }

    try {
        const url = new URL(href);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
    } catch {
        return undefined;
    }
};

const isMessageFromMenuIframe = (event: MessageEvent, menuIframeRef: RefObject<IframeResizerRef | null>): boolean =>
    event.source === menuIframeRef.current?.element?.contentWindow;

const navItems = [
    {
        key: 'accounts',
        route: (
            <NavLink to={routes.DEFAULT}>
                <FormattedMessage id='intl-msg:helpgang.sublink.accounts' />
            </NavLink>
        ),
    },
    {
        key: 'partner_integrations',
        route: (
            <NavLink to={routes.PARTNER_INTEGRATIONS}>
                <FormattedMessage id='intl-msg:helpgang.sublink.partner_integrations' />
            </NavLink>
        ),
    },
    {
        key: 'client_metrics',
        route: (
            <NavLink to={routes.CLIENT_METRICS}>
                <FormattedMessage id='intl-msg:helpgang.sublink.client_metrics' />
            </NavLink>
        ),
    },
];

if (import.meta.env.DEV) {
    navItems.push({
        key: 'users',
        route: <NavLink to={routes.USER_ADMIN_POC}>Users (POC)</NavLink>,
    });
}

const AppHeader = () => {
    const [appMenuOpen, setAppMenuOpen] = useState(false);
    const [appMenuResetKey, setAppMenuResetKey] = useState(0);
    const menuIframeRef = useRef<IframeResizerRef>(null);

    const menuOrigin = getMenuOrigin(config.menuUrl);

    // biome-ignore lint/style/noNonNullAssertion: trust
    const idToken = useAppSelector(getIdToken)!;

    const { sendMessage } = usePostMessageSender();

    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;

    const userMenuItem = (
        <DefaultUserMenu environment={import.meta.env.MODE} disableCookieModal={true} postLogout={postLogout} />
    );

    const closeAppMenu = () => {
        if (!appMenuOpen) {
            return;
        }

        document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        setAppMenuResetKey(current => current + 1);
        setAppMenuOpen(false);
    };

    useIncomingPostMessages({
        // An invalid or missing configuration must not accept messages from arbitrary origins.
        origin: menuOrigin ?? 'https://invalid-menu-origin.local',
        handler: (event: MessageEvent) => {
            const message = event.data as MenuLinkClickedMessage | undefined;
            if (
                !menuOrigin ||
                event.origin !== menuOrigin ||
                !isMessageFromMenuIframe(event, menuIframeRef) ||
                message?.type !== 'MENU_LINK_CLICKED'
            ) {
                return;
            }

            closeAppMenu();

            const navigationUrl = getNavigationUrl(message.href);
            if (navigationUrl) {
                window.location.assign(navigationUrl);
            }

            sendMessage(
                { type: 'MENU_LINK_HANDLED', requestId: message.requestId },
                event.source as WindowProxy | null,
                event.origin
            );
        },
    });

    const appNavigator = menuOrigin ? (
        <div className='iframe-wrapper'>
            <IframeResizer ref={menuIframeRef} title='Admin Menu' src={menuOrigin} className='iFrameResizer' />
        </div>
    ) : undefined;

    return (
        <ApplicationHeader
            key={appMenuResetKey}
            label={<FormattedMessage id='intl-msg:helpgang.moduleName' />}
            appNavigator={appNavigator}
            homeRoute={<Link to={config.homeRoute || ''} />}
            navItems={navItems}
            actionBarItems={[userMenuItem]}
            onToggleAppMenu={setAppMenuOpen}
        />
    );
};

export default AppHeader;
